import React from 'react';
import { motion } from 'framer-motion';
import { Target, CheckCircle2, Users } from 'lucide-react';
const steps = [{
  number: '01',
  title: 'Set a goal',
  description: 'Something real and measurable — views, growth, a milestone.',
  visual: <div className="mt-6 p-[1px] rounded-xl bg-gradient-to-b from-slate-700/50 to-slate-800/30" data-id="element-167">
        <div className="bg-slate-900/80 rounded-xl p-3 flex items-center gap-3" data-id="element-168">
          <div className="w-8 h-8 rounded-lg bg-mint-500/10 flex items-center justify-center flex-shrink-0" data-id="element-169">
            <Target className="w-4 h-4 text-mint-500" data-id="element-170" />
          </div>
          <div className="flex-1" data-id="element-171">
            <div className="text-xs font-semibold text-slate-200 mb-1" data-id="element-172">
              Hit 100K views in 7 days
            </div>
            <div className="h-1 w-full bg-slate-800 rounded-full overflow-hidden" data-id="element-173">
              <div className="h-full bg-mint-500 w-1/3 rounded-full" data-id="element-174" />
            </div>
          </div>
        </div>
      </div>
}, {
  number: '02',
  title: 'Offer paths',
  description: "Present options you'd actually take. No fake choices.",
  visual: <div className="mt-6 flex flex-col gap-2" data-id="element-175">
        <div className="px-3 py-2 rounded-lg bg-mint-500/10 border border-mint-500/20 flex items-center justify-between" data-id="element-176">
          <span className="text-xs font-semibold text-mint-400" data-id="element-177">Deep dive</span>
          <div className="w-2 h-2 rounded-full bg-mint-500" data-id="element-178" />
        </div>
        <div className="px-3 py-2 rounded-lg bg-slate-800/30 border border-slate-700/30 text-xs font-medium text-slate-400" data-id="element-179">
          Challenge format
        </div>
        <div className="px-3 py-2 rounded-lg bg-slate-800/30 border border-slate-700/30 text-xs font-medium text-slate-400" data-id="element-180">
          Collab with @Partner
        </div>
      </div>
}, {
  number: '03',
  title: 'Fans commit',
  description: 'Your audience picks a path and puts their reputation behind it.',
  visual: <div className="mt-6 p-3 rounded-xl bg-slate-800/20 border border-slate-700/40" data-id="element-181">
        <div className="flex items-center justify-between mb-3" data-id="element-182">
          <div className="flex -space-x-2" data-id="element-183">
            <div className="w-6 h-6 rounded-full bg-slate-700 border-2 border-navy-950 flex items-center justify-center text-[9px] font-bold text-slate-300" data-id="element-184">
              A
            </div>
            <div className="w-6 h-6 rounded-full bg-slate-600 border-2 border-navy-950 flex items-center justify-center text-[9px] font-bold text-slate-300" data-id="element-185">
              B
            </div>
            <div className="w-6 h-6 rounded-full bg-slate-800 border-2 border-navy-950 flex items-center justify-center" data-id="element-186">
              <Users className="w-3 h-3 text-slate-400" data-id="element-187" />
            </div>
          </div>
          <span className="text-xs font-bold text-mint-400" data-id="element-188">2.8k Takes</span>
        </div>
        <div className="flex h-1.5 w-full rounded-full overflow-hidden gap-0.5" data-id="element-189">
          <div className="h-full bg-mint-500 w-[45%]" data-id="element-190" />
          <div className="h-full bg-slate-600 w-[32%]" data-id="element-191" />
          <div className="h-full bg-slate-700 w-[23%]" data-id="element-192" />
        </div>
      </div>
}, {
  number: '04',
  title: 'Market resolves',
  description: 'The market closes based on what actually happened.',
  visual: <div className="mt-6 p-[1px] rounded-xl bg-gradient-to-r from-mint-500/30 to-wave-500/30" data-id="element-193">
        <div className="bg-slate-900 rounded-xl p-4 flex flex-col items-center justify-center text-center gap-2" data-id="element-194">
          <div className="w-8 h-8 rounded-full bg-mint-500/20 flex items-center justify-center" data-id="element-195">
            <CheckCircle2 className="w-5 h-5 text-mint-400" data-id="element-196" />
          </div>
          <div data-id="element-197">
            <div className="text-xs font-bold text-white" data-id="element-198">Market Resolved</div>
            <div className="text-[10px] text-mint-400 font-medium mt-0.5" data-id="element-199">
              Deep dive won
            </div>
          </div>
        </div>
      </div>
}];
export function IntroducingPulseMarkets() {
  return <section id="how-it-works" className="py-28 lg:py-40 bg-navy-950 relative overflow-hidden" data-id="element-200">
      {/* Background accent */}
      <div className="absolute bottom-0 left-0 w-[500px] h-[300px] bg-wave-500/[0.03] rounded-full blur-[100px] -z-10" data-id="element-201" />

      <div className="max-w-5xl mx-auto px-6" data-id="element-202">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="mb-20" data-id="element-203">
          <h2 className="text-4xl lg:text-5xl font-extrabold text-white mb-4" data-id="element-204">
            How it works
          </h2>
          <p className="text-lg text-slate-400" data-id="element-205">
            Four steps. One content arc. Better signal than any poll.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-4 gap-8 lg:gap-6 mb-20" data-id="element-206">
          {steps.map((step, index) => <motion.div key={index} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: index * 0.08
        }} className="relative group flex flex-col h-full" data-id="element-207">
              <div data-id="element-208">
                <div className="text-6xl font-extrabold text-transparent bg-clip-text bg-gradient-to-b from-slate-600 to-slate-800/60 mb-5 leading-none select-none" data-id="element-209">
                  {step.number}
                </div>
                <h3 className="text-lg font-bold text-white mb-2" data-id="element-210">
                  {step.title}
                </h3>
                <p className="text-sm text-slate-400 leading-relaxed" data-id="element-211">
                  {step.description}
                </p>
              </div>
              <div className="mt-auto pt-2" data-id="element-212">{step.visual}</div>
            </motion.div>)}
        </div>

        <motion.div initial={{
        opacity: 0
      }} whileInView={{
        opacity: 1
      }} viewport={{
        once: true
      }} className="pt-10 border-t border-slate-800/30 relative" data-id="element-213">
          {/* Enhanced content arc line */}
          <div className="absolute top-0 left-0 w-full flex justify-between -mt-[5px] px-2 sm:px-10 opacity-50" data-id="element-214">
            <div className="w-2 h-2 rounded-full bg-slate-600" data-id="element-215" />
            <div className="w-2 h-2 rounded-full bg-slate-600" data-id="element-216" />
            <div className="w-2 h-2 rounded-full bg-slate-600" data-id="element-217" />
            <div className="w-2 h-2 rounded-full bg-mint-500 shadow-[0_0_8px_rgba(80,242,181,0.6)]" data-id="element-218" />
          </div>

          <p className="text-lg text-slate-300 font-medium text-center sm:text-left" data-id="element-219">
            Each market becomes a content arc:{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-mint-400 to-wave-400 font-bold" data-id="element-220">
              setup → updates → result → next move
            </span>
          </p>
        </motion.div>
      </div>
    </section>;
}